// The phone app's walkthroughs (18-help-and-training.md): one per screen, found by the screen's
// path. The phone app has no menu, so each walkthrough says itself who can open its screen (`anyOf`).
import { canSeeWalkthrough, type Permission, type Walkthrough } from '@ecobox/domain';
import { OPS_WALKTHROUGHS } from './content';
import { OPS_TRAINING_WALKTHROUGH } from './training';

export const ALL_OPS_WALKTHROUGHS: readonly Walkthrough[] = [
  ...OPS_WALKTHROUGHS,
  OPS_TRAINING_WALKTHROUGH,
];

export interface OpsPage {
  /** The walkthrough id of the screen. */
  id: string;
  /** Matches the screen's path (App.tsx routes). */
  pattern: RegExp;
  /** A screen with a fixed path opens with it; one for a single record opens from another screen. */
  open: { path: string } | { from: string; label: string };
}

/** Every screen of the phone app, in the order My training lists them. */
export const OPS_PAGES: readonly OpsPage[] = [
  { id: 'ops-today', pattern: /^\/today$/, open: { path: '/today' } },
  { id: 'ops-load', pattern: /^\/load$/, open: { path: '/load' } },
  { id: 'ops-receive', pattern: /^\/receive$/, open: { path: '/receive' } },
  { id: 'ops-session', pattern: /^\/session\/[^/]+$/, open: { from: '/today', label: 'Today' } },
  { id: 'ops-handover', pattern: /^\/handover$/, open: { path: '/handover' } },
  {
    id: 'ops-handover-session',
    pattern: /^\/handover\/[^/]+$/,
    open: { from: '/handover', label: 'Hand over' },
  },
  { id: 'ops-yard', pattern: /^\/yard$/, open: { path: '/yard' } },
  { id: 'ops-exception', pattern: /^\/exception$/, open: { path: '/exception' } },
  { id: 'ops-scan', pattern: /^\/scan$/, open: { path: '/scan' } },
  { id: 'ops-me', pattern: /^\/me$/, open: { path: '/me' } },
  { id: 'ops-run', pattern: /^\/run\/[^/]+$/, open: { from: '/today', label: 'Today' } },
  { id: 'ops-run-load', pattern: /^\/run\/[^/]+\/load$/, open: { from: '/today', label: 'Today' } },
  {
    id: 'ops-run-stop',
    pattern: /^\/run\/[^/]+\/stop\/[^/]+$/,
    open: { from: '/today', label: 'Today' },
  },
  { id: 'ops-run-end', pattern: /^\/run\/[^/]+\/end$/, open: { from: '/today', label: 'Today' } },
  { id: 'ops-training', pattern: /^\/training$/, open: { path: '/training' } },
];

/** The screen at a path, or null (the login screen, an unknown path). */
export function opsPageForPath(pathname: string): OpsPage | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return OPS_PAGES.find((p) => p.pattern.test(path)) ?? null;
}

/** The walkthrough of the screen at a path, or null when it has none yet. */
export function walkthroughForPath(
  pathname: string,
  walkthroughs: readonly Walkthrough[] = ALL_OPS_WALKTHROUGHS,
): Walkthrough | null {
  const page = opsPageForPath(pathname);
  return page ? (walkthroughs.find((w) => w.id === page.id) ?? null) : null;
}

export interface OpsTrainingEntry {
  walkthrough: Walkthrough;
  open: OpsPage['open'];
}

/** The walkthroughs of the screens a person can open, in screen order. */
export function opsTraining(
  held: ReadonlySet<Permission>,
  walkthroughs: readonly Walkthrough[] = ALL_OPS_WALKTHROUGHS,
): OpsTrainingEntry[] {
  return OPS_PAGES.flatMap((page) => {
    const w = walkthroughs.find((x) => x.id === page.id);
    return w && canSeeWalkthrough(w, held) ? [{ walkthrough: w, open: page.open }] : [];
  });
}
